/**
 * =============================================================================
 * WaterSpriteManager - Agua animada multi-frame (en loop)
 * =============================================================================
 *
 * El agua se anima ciclando varios frames PNG. Cada frame es 128×128 px = un
 * bloque de 4×4 tiles (el generador siempre empuja agua en bloques 4×4 alineados
 * al origen de bloque, ver shared/world/worldGenerator.ts → pushWaterBlock). Por
 * eso el render se hace por BLOQUE (un sprite de 128px por bloque, no por tile).
 *
 * La animación se sincroniza con un índice de frame GLOBAL (manejado por la
 * escena), así todos los bloques de agua visibles muestran el mismo frame y el
 * océano se ve coherente.
 *
 * Frames en: /assets/world/waters/<type>_<n>.png  (n es 1-based).
 * Ver docs/WORLD_BUILDER.md.
 */

import Phaser from 'phaser';
import { ASSETS_BASE_URL } from '@shared/constants';

/** Registro extensible de tipos de agua → cantidad de frames. */
export const WATER_TYPES: Record<string, { frames: number }> = {
  water1: { frames: 4 },
};

/** ms por frame de la animación de agua (≈6 fps). */
export const WATER_FRAME_MS = 160;

/**
 * Normaliza el sprite id de un tile de agua a un tipo conocido.
 * `water_1` (mapas viejos) y `water1` → `water1`. null si no es un tipo conocido.
 */
export function getWaterTypeForSprite(spriteId: string): string | null {
  const norm = spriteId.toLowerCase().replace(/[^a-z0-9]/g, '');
  return norm in WATER_TYPES ? norm : null;
}

/** Cantidad de frames de un tipo de agua (1 si no existe). */
export function getWaterFrameCount(type: string): number {
  return WATER_TYPES[type]?.frames ?? 1;
}

/** Key de textura de un frame (0-based) de un tipo de agua. */
export function getWaterFrameTextureKey(type: string, frameIdx: number): string {
  return `water_${type}_${frameIdx}`;
}

/**
 * Precarga todos los frames de todos los tipos de agua. Llamar en preload().
 * Los archivos son 1-based (water1_1.png ... water1_N.png).
 */
export function preloadWaterFrames(scene: Phaser.Scene): void {
  for (const [type, def] of Object.entries(WATER_TYPES)) {
    for (let i = 0; i < def.frames; i++) {
      const key = getWaterFrameTextureKey(type, i);
      if (scene.textures.exists(key)) continue;
      scene.load.image(key, `${ASSETS_BASE_URL}/world/waters/${type}_${i + 1}.png`);
    }
  }
}
